import { toast } from "sonner";
import { IconDownload, IconLock, IconStarFilled, IconWifiOff } from "@tabler/icons-react";
import { CollapsibleRow } from "@/components/collapsible-row";
import { DiffBadge } from "@/components/diff-badge";
import { Button } from "@/components/ui/button";
import { NvpStatusDot, type NvpStatus } from "@/components/nvp-status-dot";
import { SectionHeader } from "@/components/ui/section-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { formatValue, valuesMatch } from "@/domain/calibration-params";
import type { NvpParameter } from "@/domain/calibration-params";
import {
  useCaptureWingCal,
  useSetWingCalState,
  useWingCalHistory,
} from "@/api/hooks/use-wing-cals";
import type { WingCalHistoryEntry, WingCalState } from "@/api/hooks/use-wing-cals";
import type { Action } from "@/providers/auth-provider";
import type { BuildId } from "@/types/identifiers";
import { formatDate, formatDateTime, timeAgo } from "@/lib/time";

function toParamMap(params: Array<Record<string, unknown>>): Map<string, NvpParameter> {
  return new Map(params.map((p) => [p.Name as string, p as unknown as NvpParameter]));
}

function WingCoefTable({
  baseValues,
  baseLabel,
  compareValues,
  compareLabel,
}: {
  baseValues: Map<string, NvpParameter>;
  baseLabel: string;
  compareValues: Map<string, NvpParameter> | null;
  compareLabel: string;
}) {
  const hasCompare = !!compareValues;
  const names = [
    ...new Set([
      ...baseValues.keys(),
      ...(compareValues ? compareValues.keys() : []),
    ]),
  ].sort();

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground/70 text-[11px] uppercase tracking-wider">
          <th className="text-left font-medium pb-1.5">Coefficient</th>
          <th className="text-right font-medium pb-1.5 whitespace-nowrap">{baseLabel}</th>
          {hasCompare && (
            <th className="text-right font-medium pb-1.5 whitespace-nowrap">{compareLabel}</th>
          )}
        </tr>
      </thead>
      <tbody>
        {names.map((name) => {
          const base = baseValues.get(name);
          const compare = compareValues?.get(name);
          const accuracy = compare?.Accuracy ?? base?.Accuracy;
          const chpDataType = compare?.ChpDataType ?? base?.ChpDataType;
          const isDiff =
            hasCompare && !valuesMatch(base?.Value, compare?.Value, accuracy, chpDataType);
          return (
            <tr key={name} className="border-t border-border/50">
              <td className="py-1 font-mono text-foreground/80">{name}</td>
              <td
                className={`py-1 text-right font-mono whitespace-nowrap ${isDiff ? "text-green-700 dark:text-green-400" : "text-foreground/50"}`}
              >
                {base ? formatValue(base.Value, base.Accuracy, base.ChpDataType) : "-"}
              </td>
              {hasCompare && (
                <td
                  className={`py-1 text-right font-mono whitespace-nowrap ${isDiff ? "text-amber-600 dark:text-amber-400" : "text-foreground/50"}`}
                >
                  {compare ? formatValue(compare.Value, compare.Accuracy, compare.ChpDataType) : "-"}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const STATE_BADGE: Record<WingCalState, string> = {
  golden: "bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400",
  initial: "bg-sky-100 dark:bg-sky-950/50 text-sky-700 dark:text-sky-400",
  draft: "bg-muted text-muted-foreground",
};

function StateBadge({ state }: { state: WingCalState }) {
  return (
    <span
      className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${STATE_BADGE[state]}`}
    >
      {state}
    </span>
  );
}

function EventMeta({ entry, dim }: { entry: WingCalHistoryEntry; dim?: boolean }) {
  // When it was RECORDED, not when the event ran - a cart push or a live read
  // can land days either side of an event's start date.
  const recorded = `Recorded ${formatDateTime(entry.created_at)} by ${entry.created_by}`;
  return (
    <>
      <span className="inline-flex items-center gap-1 text-sm font-medium">
        <IconStarFilled
        className={`h-3 w-3 ${
          dim
            ? "text-amber-500/70"
            : entry.state === "golden"
              ? "text-amber-500"
              : "text-muted-foreground/40"
        }`}
      />
        {entry.event_name}
      </span>
      <span className="text-xs text-muted-foreground whitespace-nowrap" title={recorded}>
        {dim ? formatDate(entry.created_at) : timeAgo(entry.created_at)}
        <span className="hidden sm:inline"> by {entry.created_by}</span>
      </span>
      {dim ? (
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/40">
          {entry.source}
        </span>
      ) : (
        <>
          <StateBadge state={entry.state} />
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/40">
            {entry.source}
          </span>
        </>
      )}
      <span className="text-xs text-muted-foreground/50 whitespace-nowrap">
        {entry.param_count} coefs
      </span>
    </>
  );
}

/** One measurement captured at THIS event. Values visible, promotable.
 *
 * The promote control lives in the EXPANDED panel, not the header: the header
 * is itself a <button> (CollapsibleRow), so nesting one there is invalid HTML.
 * Expanding first also means nobody promotes without seeing the coefs.
 */
function CurrentWingCalCard({
  entry,
  isBaseline,
  onSetState,
  isPending,
  canManage,
  readOnly,
}: {
  entry: WingCalHistoryEntry;
  isBaseline: boolean;
  onSetState: (calId: string, state: WingCalState) => void;
  isPending: boolean;
  canManage: boolean;
  readOnly: boolean;
}) {
  const isGolden = entry.state === "golden";
  return (
    <CollapsibleRow
      border={isGolden ? "border-l-amber-400" : "border-l-sky-300"}
      defaultExpanded={isBaseline}
      left={<EventMeta entry={entry} />}
      right={<DiffBadge count={entry.diff_count} />}
    >
      {canManage && !readOnly && (
        <div className="flex items-center justify-between gap-3 pb-2.5 mb-2.5 border-b border-border/50">
          <span className="text-xs text-muted-foreground">
            {isGolden
              ? "This is the golden restored to the wing."
              : "Captured from the wing, not yet accepted."}
          </span>
          <Button
            variant={isGolden ? "secondary" : "outline"}
            size="sm"
            className="text-xs h-7"
            disabled={isPending}
            onClick={() => onSetState(entry.id, "golden")}
          >
            {isGolden ? "Unset golden" : "Mark golden"}
          </Button>
        </div>
      )}
      <WingCoefTable
        baseValues={toParamMap(entry.parameters as Array<Record<string, unknown>>)}
        baseLabel="Value"
        compareValues={null}
        compareLabel=""
      />
    </CollapsibleRow>
  );
}

/** A historic event's wing cal, compared against this event's. */
function HistoryRow({
  entry,
  currentValues,
  currentLabel,
}: {
  entry: WingCalHistoryEntry;
  currentValues: Map<string, NvpParameter> | null;
  currentLabel: string;
}) {
  const badge =
    entry.diff_count == null ? null : entry.diff_count > 0 ? (
      <span className="text-xs font-medium text-amber-600 dark:text-amber-400">{entry.diff_count} changed</span>
    ) : (
      <span className="text-xs font-medium text-green-600 dark:text-green-400">Identical</span>
    );

  return (
    <CollapsibleRow border="border-l-border" left={<EventMeta entry={entry} dim />} right={badge}>
      <WingCoefTable
        baseValues={toParamMap(entry.parameters as Array<Record<string, unknown>>)}
        baseLabel={entry.event_name}
        compareValues={currentValues}
        compareLabel={currentLabel}
      />
    </CollapsibleRow>
  );
}

interface WingCalHistorySectionProps {
  buildId: BuildId;
  can: (action: Action) => boolean;
  wingNvpStatus: NvpStatus;
  readOnly?: boolean;
}

export function WingCalHistorySection({
  buildId,
  can,
  wingNvpStatus,
  readOnly = false,
}: WingCalHistorySectionProps) {
  const { data, isLoading } = useWingCalHistory(buildId);
  const capture = useCaptureWingCal(buildId);
  const setState = useSetWingCalState(buildId);

  const current = data?.current ?? [];
  const entries = data?.entries ?? [];
  const canManage = can("manage-calibrations");

  // The server decides which row every diff_count was measured against and
  // flags it. Re-deriving that rule here is how the diffs and the column they
  // are rendered under quietly stop agreeing.
  const baseline = current.find((c) => c.is_baseline) ?? null;
  const baselineValues = baseline
    ? toParamMap(baseline.parameters as Array<Record<string, unknown>>)
    : null;
  const baselineLabel = baseline
    ? `${baseline.event_name} (${baseline.state})`
    : "Current";

  const isOffline = wingNvpStatus !== "online";

  const handleFetch = () => {
    capture.mutate(undefined, {
      onSuccess: () => toast.success("Wing cal captured from NVP"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleSetState = (calId: string, state: WingCalState) => {
    setState.mutate(
      { calId, state },
      {
        onSuccess: (cal) =>
          toast.success(
            cal.state === "golden" ? "Marked as golden" : `Moved to ${cal.state}`,
          ),
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <>
      <div className="mt-6 flex items-center justify-between gap-4">
        <SectionHeader className="mb-0">Wing Cals</SectionHeader>

        {canManage && (
          <div className="flex items-center gap-3">
            {readOnly && <span className="text-xs text-muted-foreground">Unlock first</span>}
            <NvpStatusDot status={wingNvpStatus} label="Wing NVP" />
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={handleFetch}
              disabled={capture.isPending || readOnly || isOffline}
            >
              {capture.isPending
                ? <span className="mr-1.5 h-3 w-3 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
                : readOnly
                  ? <IconLock className="mr-1.5 h-3 w-3" />
                  : isOffline
                    ? <IconWifiOff className="mr-1.5 h-3 w-3" />
                    : <IconDownload className="mr-1.5 h-3 w-3" />
              }
              {capture.isPending ? "Fetching\u2026" : "Fetch from NVP"}
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-11 w-full rounded-lg" />
          ))}
        </div>
      ) : current.length === 0 && entries.length === 0 ? (
        <EmptyState message="No wing cals recorded for this boat yet." className="mt-4" />
      ) : (
        <>
          {current.length > 0 ? (
            <div className="mt-4 space-y-1.5">
              {current.map((entry) => (
                <CurrentWingCalCard
                  key={entry.id}
                  entry={entry}
                  isBaseline={entry.id === baseline?.id}
                  onSetState={handleSetState}
                  isPending={setState.isPending}
                  canManage={canManage}
                  readOnly={readOnly}
                />
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">No wing cal recorded at this event.</p>
          )}

          {entries.length > 0 && (
            <div className="mt-8">
              <SectionHeader>Comparisons</SectionHeader>
              <div className="space-y-1.5">
                {entries.map((entry) => (
                  <HistoryRow
                    key={entry.id}
                    entry={entry}
                    currentValues={baselineValues}
                    currentLabel={baselineLabel}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
